import React, { useEffect, useState } from 'react';
import { Card, Col, Form, Row, Modal, Button, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useParams } from "react-router-dom";
import useToken from '../../../hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import DatePicker from 'react-datepicker';
import IconButton from 'components/common/IconButton';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { FiUser, FiCalendar, FiClock, FiWifi, FiArrowRight, FiUserCheck, FiRotateCcw, FiLink, FiSearch, FiDownload, FiTrash2, FiFileText } from 'react-icons/fi';
import ImagenPerfil from '../../../components/common/ImagenPerfil';



const initialAssetState = {
    vNumeroRegistro: '',
    vNombreActivo: '',
    vDescripcion: '',
    idCategoria: 0,
    vNumeroSerie: '',
    vUbicacionActivo: '',
    dCostoAdquisicion: '',
    dFechaCompra: null,
    vImageName: '',
    dFechaUso: null,
    idEmpleadoResponsable: 0,
    iEstadoActivo: null,
    idGasto: null
};

const AssetForm = () => {

    const { token, setToken, idCatTipoUsuario } = useToken();
    const navigate = useNavigate();
    const { id } = useParams();
    const { showErrorToast, showSuccessToast } = useToast();


    const [cargando, setCargando] = useState(false);
    const [cargandoEmpleados, setCargandoEmpleados] = useState(false);
    const [empleados, setEmpleados] = useState([]);
    const [historialAsignaciones, setHistorialAsignaciones] = useState([]);
    const [responsableActual, setResponsableActual] = useState(0);


    const [asset, setAsset] = useState(initialAssetState);
    const [errores, setErrores] = useState({});
    const [editando, setEditando] = useState(id ? false : true);
    const [assetOriginal, setAssetOriginal] = useState(null);

    const [mostrarModalGasto, setMostrarModalGasto] = useState(false);
    const [searchTermGasto, setSearchTermGasto] = useState('');
    const [gastosEncontrados, setGastosEncontrados] = useState([]);
    const [buscandoGastos, setBuscandoGastos] = useState(false);
    const [descargandoNota, setDescargandoNota] = useState(false);

    /**
     * Búsqueda de gastos para vincular
     */
    const buscarGastos = async (termino) => {
        setBuscandoGastos(true);
        try {
            const response = await fetch("/api/ActivoFijo/BuscarGastosParaVincular", {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ searchTerm: termino })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const detalles = JSON.parse(data.detalles);
                    setGastosEncontrados(detalles.table2 || []);
                } else {
                    showErrorToast(data.vMessage);
                }
            } else {
                showErrorToast("Error al buscar gastos.");
            }
        } catch (error) {
            showErrorToast("Error en la petición de búsqueda.");
        } finally {
            setBuscandoGastos(false);
        }
    };

    // Debounce manual para la búsqueda
    useEffect(() => {
        if (!mostrarModalGasto) return;
        const delayDebounceFn = setTimeout(() => {
            buscarGastos(searchTermGasto);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTermGasto, mostrarModalGasto]);

    const handleSeleccionarGasto = (gasto) => {
        // Calcular valor unitario del activo: (Importe - Descuento) / Cantidad
        const importe = gasto.dGastoImporte != null ? parseFloat(gasto.dGastoImporte) : 0;
        const descuento = gasto.dGastoDescuento != null ? parseFloat(gasto.dGastoDescuento) : 0;
        const cantidad = gasto.dGastoCantidad != null ? parseFloat(gasto.dGastoCantidad) : 1;
        const valorCalculado = cantidad > 0 ? (importe - descuento) / cantidad : 0;

        setAsset(prev => ({
            ...prev,
            idGasto: gasto.idGasto,
            vNombreActivo: gasto.vGastoDescripcion ? gasto.vGastoDescripcion.toUpperCase() : '',
            dCostoAdquisicion: valorCalculado.toFixed(2),
            dFechaCompra: gasto.dFechaGasto ? new Date(gasto.dFechaGasto) : null,
            // Guardar detalles del gasto para la visualización de la tarjeta
            vGastoDescripcion: gasto.vGastoDescripcion,
            vGastoTipoGasto: gasto.vGastoTipoGasto,
            vGastoResponsable: gasto.vGastoResponsable,
            dFechaGasto: gasto.dFechaGasto,
            vGastoFormaPago: gasto.vGastoFormaPago,
            dGastoCantidad: gasto.dGastoCantidad,
            dGastoPrecioUnitario: gasto.dGastoPrecioUnitario,
            dGastoIva: gasto.dGastoIva,
            dGastoIvaRet: gasto.dGastoIvaRet,
            dGastoIsrRet: gasto.dGastoIsrRet,
            dGastoDescuento: gasto.dGastoDescuento,
            dGastoImporte: gasto.dGastoImporte,
            dGastoTotal: gasto.dGastoTotal,
            vGastoMoneda: gasto.vGastoMoneda,
            dGastoTipoCambio: gasto.dGastoTipoCambio,
            bHasNotaVenta: gasto.bHasNotaVenta,
            vGastoNotaVenta_Name: gasto.vGastoNotaVenta_Name,
            DetallesCuenta: gasto.DetallesCuenta,
            vRfcEmisor: gasto.vRfcEmisor,
            vRazonSocialEmisor: gasto.vRazonSocialEmisor,
            vUUIDFactura: gasto.vUUIDFactura,
            idFacturaRecibida: gasto.idFacturaRecibida
        }));

        setMostrarModalGasto(false);
        showSuccessToast("Gasto vinculado correctamente.");
    };

    const handleDesvincularGasto = () => {
        setAsset(prev => ({
            ...prev,
            idGasto: null,
            // Conservamos los valores autocompletados pero los campos ya no se bloquearán
            // y limpiamos los detalles de la tarjeta del gasto
            vGastoDescripcion: null,
            vGastoTipoGasto: null,
            vGastoResponsable: null,
            dFechaGasto: null,
            vGastoFormaPago: null,
            dGastoCantidad: null,
            dGastoPrecioUnitario: null,
            dGastoIva: null,
            dGastoIvaRet: null,
            dGastoIsrRet: null,
            dGastoDescuento: null,
            dGastoImporte: null,
            dGastoTotal: null,
            vGastoMoneda: null,
            dGastoTipoCambio: null,
            bHasNotaVenta: null,
            vGastoNotaVenta_Name: null,
            DetallesCuenta: null,
            vRfcEmisor: null,
            vRazonSocialEmisor: null,
            vUUIDFactura: null,
            idFacturaRecibida: null
        }));
        showSuccessToast("Gasto desvinculado correctamente.");
    };

    const handleDescargarNotaVenta = async (idGasto) => {
        setDescargandoNota(true);
        try {
            const response = await fetch(`/api/Gastos/ObtenerNotaVenta?idGasto=${idGasto}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
            });

            if (response.ok) {
                const data = await response.json();

                if (data.iResponseType === 1) {
                    const detalles = JSON.parse(data.detalles);
                    const { vNotaVenta_Base64, vNotaVenta_Name } = detalles.table2[0];

                    const byteCharacters = atob(vNotaVenta_Base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: 'application/octet-stream' });

                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = vNotaVenta_Name;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    URL.revokeObjectURL(link.href);
                } else {
                    showErrorToast(data.vMessage || 'Error al obtener nota de venta');
                }
            } else {
                showErrorToast('Error en la petición: ' + response.statusText);
            }
        } catch (error) {
            showErrorToast('Error al descargar nota de venta');
            console.error('Error al descargar nota de venta:', error);
        } finally {
            setDescargandoNota(false);
        }
    };

    /**
     * Funcion para editar el activo fijo
     */
    const handleEditar = () => {
        setAssetOriginal(asset);
        setEditando(true);
    };

    /**
     * Funcion para cancelar la edición
     */
    const handleCancelar = () => {
        if (assetOriginal) setAsset(assetOriginal);
        setEditando(false);
    };



    /**
    * Funcion para obtener los activos fijos por ID
    */
    const obtenerActivoporID = async (idActivoFijo) => {

        console.log("idActivoFijo: ", idActivoFijo);

        setCargando(true);

        var dataToSend = {};
        dataToSend.idActivoFijo = idActivoFijo;
        setAsset(initialAssetState); // Usar estado inicial en vez de objeto vacío


        const response = await fetch("/api/ActivoFijo/ObtenerActivoFijoPorID",
            {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dataToSend)
            })
            .catch((error) => {
                showErrorToast('Hubo un problema con la peticion Fetch:' + error);
                //console.log('Hubo un problema con la peticion Fetch:' + error);

            });

        if (response.ok) {
            setCargando(false);
            //console.log("response: ", response);
            const data = await response.json();
            //console.log("data: ", data);


            if (data.iResponseType == 1) {

                var ObjDetalles = JSON.parse(data.detalles);
                console.log("data: ", ObjDetalles);

                setAsset(prev => ({
                    ...prev,
                    ...ObjDetalles.table2[0] // Sobrescribe solo los campos que llegan del servidor
                }));

                // Obtiene el responsable actua
                setResponsableActual(ObjDetalles.table2[0].idEmpleadoResponsable);
            } else {
                showErrorToast(data.vMessage);
            }

        }
        else {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
        }
    };




    /**
    * Funcion para el historial de asiganciones
    */
    const obtenerHistorialAsignaciones = async (idActivoFijo) => {
        setCargando(true);


        var dataToSend = {};
        dataToSend.idActivoFijo = idActivoFijo;


        const response = await fetch("/api/ActivoFijo/ObtenerHistorialAsignaciones",
            {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dataToSend)
            })
            .catch((error) => {
                showErrorToast('Hubo un problema con la peticion Fetch:' + error);
                //console.log('Hubo un problema con la peticion Fetch:' + error);

            });

        if (response.ok) {
            setCargando(false);
            //console.log("response: ", response);
            const data = await response.json();
            //console.log("data: ", data);


            if (data.iResponseType == 1) {
                var ObjDetalles = JSON.parse(data.detalles);

                console.log("ObjDetalles: historiual de asignaciones     ", ObjDetalles);

                setHistorialAsignaciones(ObjDetalles.table2 || []);


            } else {
                showErrorToast(data.vMessage);
            }

        }
        else {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
        }
    };

    /**
    * Funcion para manejar los cambios en el formulario
    */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setAsset(prev => ({
            ...prev,
            [name]: value
        }));
    };

    /**
     * Funcion validar campos
     */
    const validarCampos = () => {
        const nuevosErrores = {};
        if (!asset.vNumeroRegistro) nuevosErrores.vNumeroRegistro = "El n\u00famero de registro es requerido";
        if (!asset.vNombreActivo) nuevosErrores.vNombreActivo = "El nombre del activo es requerido";
        if (!asset.vDescripcion) nuevosErrores.vDescripcion = "La descripci\u00f3n es requerida";
        if (!asset.dCostoAdquisicion) {
            nuevosErrores.dCostoAdquisicion = "El valor es requerido";
        } else if (Number(asset.dCostoAdquisicion) < 0) {
            nuevosErrores.dCostoAdquisicion = "El valor no puede ser negativo";
        }
        if (!asset.idCategoria) nuevosErrores.idCategoria = "La categor\u00eda es requerida";
        if (!asset.vNumeroSerie) nuevosErrores.vNumeroSerie = "El n\u00famero de serie es requerido";
        if (!asset.idEmpleadoResponsable) nuevosErrores.idEmpleadoResponsable = "El responsable es requerido";
        if (!asset.dFechaUso) nuevosErrores.dFechaUso = "La fecha de uso es requerida";
        if (!asset.dFechaCompra) nuevosErrores.dFechaCompra = "La fecha de compra es requerida";
        if (!asset.vUbicacionActivo) nuevosErrores.vUbicacionActivo = "La ubicaci\u00f3n es requerida";
        if (asset.iEstadoActivo === undefined || asset.iEstadoActivo === null) nuevosErrores.iEstadoActivo = "El estado es requerido";
        return nuevosErrores;
    };



    /**
     * Funcion para formulario
     */
    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        const nuevosErrores = validarCampos();
        setErrores(nuevosErrores);
        if (Object.keys(nuevosErrores).length > 0) {
            // Hay errores, no enviar
            return;
        }


        if (asset.idActivoFijo === 0 || asset.idActivoFijo === null || asset.idActivoFijo === undefined) {
            guardarActualizarActivoFijo(1);
        } else {
            guardarActualizarActivoFijo(2);
        }


    };

    /**
    * Funcion para guardar o actualizar los activos fijos
    */
    const guardarActualizarActivoFijo = async (action) => {

        setCargando(true);

        const dataToSend = {
            idActivoFijo: id && id !== "null" && id !== "undefined" ? id : 0,
            vNombreActivo: asset.vNombreActivo,
            vNumeroSerie: asset.vNumeroSerie,
            vNumeroRegistro: asset.vNumeroRegistro,
            idCategoria: asset.idCategoria,
            dFechaUso: asset.dFechaUso ? new Date(asset.dFechaUso).toISOString() : null,
            dFechaCompra: asset.dFechaCompra ? new Date(asset.dFechaCompra).toISOString() : null,
            dCostoAdquisicion: asset.dCostoAdquisicion ? Number(asset.dCostoAdquisicion) : null,
            vUbicacionActivo: asset.vUbicacionActivo,
            iEstadoActivo: asset.iEstadoActivo,
            idEmpleadoResponsable: asset.idEmpleadoResponsable,
            vImageName: asset.vImageName || "default.jpg",
            vDescripcion: asset.vDescripcion || "Sin descripción",
            idResponsableActual: responsableActual,
            idGasto: asset.idGasto ? Number(asset.idGasto) : null
        };
        console.log("Enviando datos:", action);

        setAsset(initialAssetState);
        const response = await fetch("/api/ActivoFijo/GuardarActualizarActivoFijo",
            {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dataToSend)
            })
            .catch((error) => {
                showErrorToast('Hubo un problema con la peticion Fetch:' + error);
                //console.log('Hubo un problema con la peticion Fetch:' + error);

            });

        if (response.ok) {
            //console.log("response: ", response);
            const data = await response.json();
            //console.log("data: ", data);


            if (data.iResponseType == 1) {
                console.log("Enviando datos:", action);

                if (action === 1) {
                    showSuccessToast("Activo fijo registrado correctamente");
                    navigate("/administracion/activo-fijo/");
                } else {
                    setCargando(false);


                    obtenerActivoporID(id);
                    obtenerHistorialAsignaciones(id);
                    setEditando(false);
                    showSuccessToast("Activo fijo actualizado correctamente");
                }

            } else {
                showErrorToast(data.vMessage);
                setCargando(false);
                setEditando(false);

            }

        }
        else {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
                setCargando(false);
                setEditando(false);
            }
        }

    };

    /**
     * Funcion para obtener los empleados
     */
    const obtenerListaEmpleados = async () => {
        setCargandoEmpleados(true);
        const response = await fetch("/api/ActivoFijo/ObtenerEmpleados",
            {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            })
            .catch((error) => {
                showErrorToast('Hubo un problema con la peticion Fetch:' + error);
                //console.log('Hubo un problema con la peticion Fetch:' + error);

            });

        if (response && response.ok) {
            setCargandoEmpleados(false);
            //console.log("response: ", response);
            const data = await response.json();
            //console.log("data: ", data);


            if (data.iResponseType == 1) {
                var ObjDetalles = JSON.parse(data.detalles);

                console.log("ObjDetalles: ", ObjDetalles);

                setEmpleados(ObjDetalles.table2);



            } else {
                showErrorToast(data.vMessage);
                setCargandoEmpleados(false);
            }

        }
        else {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
                setCargandoEmpleados(false);
            }
        }
    };

    /**
    * Verifica los permisos
    */
    const checkPermisos = async () => {

        const response = await fetch("/api/Administradores/checkPermisos",
            {
                method: 'POST',
                headers:
                {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            }).catch((error) => {
                showErrorToast('Hubo un problema con la peticion Fetch:' + error);

                console.log('Hubo un problema con la peticion Fetch:' + error);
            });

        if (response.ok) {
            const responseType = await response.json();
            console.log(responseType);

            if (responseType.iResponseType == 1) {

                //ok


            } else {
                navigate("/");
            }

        }
        else {
            navigate("/");
        }
    }


    /**
    * Se ejecuta al cargar el componente
    */
    useEffect(() => {

        if (idCatTipoUsuario != 1) {
            navigate("/");
        }

        checkPermisos();
        obtenerListaEmpleados();
        if (id) {
            obtenerActivoporID(id);
            obtenerHistorialAsignaciones(id);
        }

    }, []);

    return (
        <>


            <Card className="mb-3 shadow-sm">
                <Card.Body>
                    <Row className="align-items-center">
                        {/* Botón regresar */}
                        <Col xs="auto">
                            <IconButton
                                onClick={() => navigate(-1)}
                                variant="falcon-default"
                                size="sm"
                                icon="arrow-left"
                            />
                        </Col>

                        {/* Título */}
                        <Col className="flex-grow-1">
                            <h4 className="mb-0 text-nowrap ms-2">
                                {
                                    <>
                                        <span className="d-none d-sm-inline ms-1">
                                            {id ? "Actualizaci\u00F3n de Activo Fijo" : "Registro de Nuevo Activo Fijo"}
                                        </span>
                                        <span className="d-inline d-sm-none ms-1">
                                            {id ? "Actualizaci\u00F3n" : "Registro"}
                                        </span>
                                    </>
                                }
                            </h4>
                        </Col>

                        {/* Botones de acción - derecha */}
                        <Col xs="auto" className="text-end">
                            {!editando ? (
                                <IconButton
                                    onClick={handleEditar}
                                    variant="falcon-primary"
                                    size="sm"
                                    icon="pencil-alt"
                                    transform="shrink-2"
                                >
                                    <span className="d-none d-xl-inline-block ms-1">Editar detalles</span>
                                </IconButton>
                            ) : (
                                <>
                                    <IconButton
                                        variant="falcon-success"
                                        size="sm"
                                        icon="check"
                                        type="submit"
                                        disabled={cargando}
                                        className="me-2 mt-3-xs"
                                        onClick={handleSubmit}
                                    >
                                        {cargando ? (
                                            <span className="d-none d-xl-inline-block ms-1">Guardando...</span>
                                        ) : (
                                            <span className="d-none d-xl-inline-block ms-1">Guardar</span>
                                        )}
                                    </IconButton>
                                    {id && (
                                        <IconButton
                                            variant="falcon-secondary"
                                            size="sm"
                                            icon="times"
                                            type="button"
                                            onClick={handleCancelar}
                                            disabled={cargando}
                                            className="mt-3-xs"
                                        >
                                            <span className="d-none d-xl-inline-block ms-1">Cancelar</span>
                                        </IconButton>
                                    )}
                                </>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* FORMULARIO CON MATERIAL UI: ESTRUCTURA PERSONALIZADA Y TODOS LOS CAMPOS */}
            {
                cargando || cargandoEmpleados ? (
                    <Card className="shadow-sm mb-3">
                        <Card.Body>
                            {/* Simulación de tabla con Skeleton */}
                            <Skeleton height={24} width={180} style={{ marginBottom: 12 }} /> {/* Cabecera */}
                            {[...Array(5)].map((_, i) => (
                                <Skeleton key={i} height={40} style={{ marginBottom: 16, borderRadius: 6 }} />
                            ))}
                        </Card.Body>
                    </Card>
                ) : (
                    <>
                        <Form onSubmit={handleSubmit}>
                            <Card className="shadow-sm">
                                <Card.Body>
                                    {editando && (!asset.idGasto || asset.idGasto === 0) && (
                                        <div className="d-flex justify-content-end mb-3">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => {
                                                    setSearchTermGasto('');
                                                    setGastosEncontrados([]);
                                                    setMostrarModalGasto(true);
                                                }}
                                            >
                                                <FiLink className="me-1" /> Vincular Gasto Existente
                                            </Button>
                                        </div>
                                    )}

                                    {/* Primera fila */}
                                    <Row className="gy-3">
                                        <Col xs={6} sm={6} md={3}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Nombre del Activo</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    autoComplete="off"
                                                    onKeyUp={event => { event.target.value = event.target.value.toUpperCase() }}
                                                    placeholder="Ingresa el nombre del activo"
                                                    name="vNombreActivo"
                                                    value={asset.vNombreActivo ?? ""}
                                                    onChange={handleChange}
                                                    disabled={!editando || (asset.idGasto && asset.idGasto !== 0)}
                                                />
                                                {errores.vNombreActivo && <div className="text-danger">{errores.vNombreActivo}</div>}
                                            </Form.Group>
                                        </Col>
                                        <Col xs={6} sm={6} md={3}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">N&uacute;mero de serie</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    autoComplete="off"
                                                    onKeyUp={event => { event.target.value = event.target.value.toUpperCase() }}
                                                    placeholder="Ingresa el n&uacute;mero de serie"
                                                    name="vNumeroSerie"
                                                    value={asset.vNumeroSerie ?? ""}
                                                    onChange={handleChange}
                                                    disabled={!editando}
                                                />

                                                {errores.vNumeroSerie && <div className="text-danger">{errores.vNumeroSerie}</div>}
                                            </Form.Group>
                                        </Col>


                                        <Col xs={6} sm={6} md={3}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">
                                                    <span className="d-none d-xl-inline-block ms-1">N&uacute;mero de registro</span>
                                                    <span className="d-xl-none ms-1 " >N&uacute;mero de regis...</span>
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    autoComplete="off"
                                                    onKeyUp={event => { event.target.value = event.target.value.toUpperCase() }}
                                                    placeholder="Ingresa el n&uacute;mero del registro"
                                                    name="vNumeroRegistro"
                                                    value={asset.vNumeroRegistro ?? ""}
                                                    onChange={handleChange}
                                                    disabled={!editando}
                                                />
                                                {errores.vNumeroRegistro && <div className="text-danger">{errores.vNumeroRegistro}</div>}
                                            </Form.Group>
                                        </Col>
                                        <Col xs={6} sm={6} md={3}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Valor</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    autoComplete="off"
                                                    placeholder="Ingresa el valor del activo"
                                                    name="dCostoAdquisicion"
                                                    value={asset.dCostoAdquisicion ?? ""}
                                                    onChange={handleChange}
                                                    disabled={!editando || (asset.idGasto && asset.idGasto !== 0)}
                                                    min={0}
                                                    onKeyDown={e => {
                                                        if (e.key === '-' || e.key === 'e') {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                />
                                                {errores.dCostoAdquisicion && <div className="text-danger">{errores.dCostoAdquisicion}</div>}
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    {/* Segunda fila */}
                                    <Row className="gy-3 mt-2">
                                        <Col xs={6} md={6} sm={12}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Descripci&oacute;n</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    autoComplete="off"
                                                    onKeyUp={event => { event.target.value = event.target.value.toUpperCase() }}
                                                    placeholder="Ingresa una descripci&oacute;n"
                                                    name="vDescripcion"
                                                    value={asset.vDescripcion ?? ""}
                                                    onChange={handleChange}
                                                    disabled={!editando}
                                                />
                                                {errores.vDescripcion && <div className="text-danger">{errores.vDescripcion}</div>}
                                            </Form.Group>
                                        </Col>

                                        <Col xs={6} md={6} sm={12}>
                                            <Row>
                                                <Col xs={6} md={6} sm={6}>
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Categor&iacute;a</Form.Label>
                                                        <Form.Control
                                                            as="select"
                                                            name="idCategoria"
                                                            value={asset.idCategoria ?? 0}
                                                            onChange={handleChange}
                                                            disabled={!editando}
                                                        >
                                                            <option value="">Selecciona una categor&iacute;a</option>
                                                            <option value="1">Electr&oacute;nica</option>
                                                            <option value="2">Mobiliario</option>
                                                            <option value="3">Veh&iacute;culos</option>
                                                            <option value="4">Inmuebles</option>
                                                            <option value="5">Otros</option>
                                                        </Form.Control>
                                                        {errores.idCategoria && <div className="text-danger">{errores.idCategoria}</div>}
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={6} md={6} sm={6}>
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Estado</Form.Label>
                                                        <Form.Control
                                                            as="select"
                                                            name="iEstadoActivo"
                                                            value={asset.iEstadoActivo ?? null}
                                                            onChange={handleChange}
                                                            disabled={!editando}
                                                        >
                                                            <option value="">Selecciona el estado</option>
                                                            <option value="0">0 - Inservible</option>
                                                            <option value="1">1 - Activo</option>
                                                            <option value="2">2 - En Reparaci&oacute;n</option>
                                                        </Form.Control>
                                                        {errores.iEstadoActivo && <div className="text-danger">{errores.iEstadoActivo}</div>}
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                    {/* Tercera fila */}
                                    <Row className="gy-3 mt-2">
                                        <Col xs={6} sm={6} md={3} xl={3}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Responsable</Form.Label>
                                                <Form.Control
                                                    as="select"
                                                    name="idEmpleadoResponsable"
                                                    value={asset.idEmpleadoResponsable ?? 0}
                                                    onChange={handleChange}
                                                    disabled={!editando}
                                                >
                                                    <option value="">Todos los empleados</option>
                                                    {empleados.map((empleado, index) => (
                                                        <option key={index} value={empleado.idUsuario}>
                                                            {empleado.vNombre} {empleado.vApellidos}
                                                        </option>
                                                    ))}
                                                </Form.Control>
                                                {errores.idEmpleadoResponsable && <div className="text-danger">{errores.idEmpleadoResponsable}</div>}
                                            </Form.Group>
                                        </Col>
                                        <Col xs={6} sm={6} md={3} xl={3}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Ubicaci&oacute;n</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    autoComplete="off"
                                                    onKeyUp={event => { event.target.value = event.target.value.toUpperCase() }}
                                                    placeholder="Ingresa la ubicaci&oacute;n"
                                                    name="vUbicacionActivo"
                                                    value={asset.vUbicacionActivo ?? ""}
                                                    onChange={handleChange}
                                                    disabled={!editando}
                                                />
                                                {errores.vUbicacionActivo && <div className="text-danger">{errores.vUbicacionActivo}</div>}
                                            </Form.Group>
                                        </Col>
                                        <Col md={6} xs={12}>
                                            <Row>
                                                <Col md={6} xs={12}>
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Fecha de Compra</Form.Label>
                                                        <DatePicker
                                                            selected={asset.dFechaCompra ? new Date(asset.dFechaCompra) : null}
                                                            dateFormat="dd MMM yyyy"
                                                            onChange={(newDate) =>
                                                                setAsset((prev) => ({ ...prev, dFechaCompra: newDate ? new Date(newDate) : null }))
                                                            }
                                                            className="form-control"
                                                            wrapperClassName="w-100"
                                                            placeholderText="Selecciona la fecha de compra"
                                                            disabled={!editando || (asset.idGasto && asset.idGasto !== 0)}
                                                        />
                                                        {errores.dFechaCompra && (
                                                            <div className="text-danger">{errores.dFechaCompra}</div>
                                                        )}
                                                    </Form.Group>
                                                </Col>

                                                <Col md={6} xs={12}>
                                                    <Form.Group>
                                                        <Form.Label className="fw-semibold">Fecha de Uso</Form.Label>
                                                        <br />
                                                        <DatePicker
                                                            selected={asset.dFechaUso ? new Date(asset.dFechaUso) : null}
                                                            dateFormat="dd MMM yyyy"
                                                            onChange={newDate => setAsset(prev => ({ ...prev, dFechaUso: newDate ? new Date(newDate) : null }))}
                                                            wrapperClassName="w-100"
                                                            placeholderText="Selecciona la fecha de uso"
                                                            className="form-control"

                                                            disabled={!editando}
                                                        />
                                                        {errores.dFechaUso && <div className="text-danger">{errores.dFechaUso}</div>}
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>

                                    {/* Tarjeta de Gasto Vinculado */}
                                    {asset.idGasto && asset.idGasto !== 0 && (
                                        <Row className="mt-4">
                                            <Col md={12}>
                                                <div className="border rounded p-3 bg-light shadow-sm">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <h6 className="mb-0 text-primary fw-bold">
                                                            <FiLink className="me-1" /> Gasto Vinculado #{asset.idGasto}
                                                        </h6>
                                                        {editando && (
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={handleDesvincularGasto}
                                                                title="Desvincular Gasto"
                                                            >
                                                                <FiTrash2 className="me-1" /> Desvincular Gasto
                                                            </Button>
                                                        )}
                                                    </div>

                                                    <Row className="g-3">
                                                        <Col md={8}>
                                                            <div className="mb-2">
                                                                <strong>Concepto:</strong> {asset.vGastoDescripcion || asset.vNombreActivo}
                                                            </div>
                                                            <div className="text-muted small mb-1">
                                                                <strong>Categoría:</strong> {asset.vGastoTipoGasto || 'No especificada'}
                                                            </div>
                                                            <div className="text-muted small mb-1">
                                                                <strong>Forma de Pago:</strong> {asset.vGastoFormaPago || 'No especificada'}
                                                            </div>
                                                            {asset.DetallesCuenta && (
                                                                <div className="text-muted small mb-1">
                                                                    <strong>Cuenta:</strong> <Badge bg="light" text="dark" className="border">{asset.DetallesCuenta}</Badge>
                                                                </div>
                                                            )}
                                                            {asset.vGastoResponsable && (
                                                                <div className="text-muted small mb-1">
                                                                    <strong>Responsable:</strong> {asset.vGastoResponsable}
                                                                </div>
                                                            )}
                                                            {(asset.vRfcEmisor || asset.vRazonSocialEmisor) && (
                                                                <div className="text-muted small mb-1">
                                                                    <strong>Emisor:</strong> {asset.vRfcEmisor}{asset.vRfcEmisor && asset.vRazonSocialEmisor && ' - '}{asset.vRazonSocialEmisor}
                                                                </div>
                                                            )}
                                                            {asset.vUUIDFactura && asset.idFacturaRecibida > 0 && (
                                                                <div className="mt-2 small">
                                                                    <strong>
                                                                        <FiFileText className="me-1 text-secondary" /> Factura:
                                                                    </strong>
                                                                    <a
                                                                        href={`/contabilidad/centro-xml/facturas-recibidas/visualizacion/${asset.idFacturaRecibida}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="ms-1 font-monospace text-primary text-decoration-none"
                                                                        style={{ wordBreak: 'break-all' }}
                                                                    >
                                                                        {asset.vUUIDFactura}
                                                                    </a>
                                                                </div>
                                                            )}
                                                            {asset.bHasNotaVenta === 1 && (
                                                                <div className="mt-3">
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        onClick={() => handleDescargarNotaVenta(asset.idGasto)}
                                                                        disabled={descargandoNota}
                                                                    >
                                                                        <FiDownload className="me-1" />
                                                                        {descargandoNota ? 'Descargando...' : 'Ver Nota de Venta'}
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </Col>
                                                        <Col md={4} className="text-md-end border-start-md">
                                                            <div className="mb-2">
                                                                <span className="text-muted d-block small">Total del Gasto</span>
                                                                <h4 className="mb-0 fw-bold text-dark">
                                                                    {asset.dGastoTotal != null ? Number(asset.dGastoTotal).toLocaleString("es-MX", {
                                                                        style: "currency",
                                                                        currency: asset.vGastoMoneda || "MXN"
                                                                    }) : "$0.00"}
                                                                </h4>
                                                            </div>
                                                            {asset.dGastoCantidad && (
                                                                <div className="text-muted small mb-1">
                                                                    <strong>Cantidad:</strong> {parseFloat(asset.dGastoCantidad).toLocaleString("es-MX")}
                                                                </div>
                                                            )}
                                                            {asset.dGastoPrecioUnitario && (
                                                                <div className="text-muted small mb-1">
                                                                    <strong>P. Unitario:</strong> {Number(asset.dGastoPrecioUnitario).toLocaleString("es-MX", { style: "currency", currency: asset.vGastoMoneda || "MXN" })}
                                                                </div>
                                                            )}
                                                            {asset.dGastoDescuento != null && Number(asset.dGastoDescuento) > 0 && (
                                                                <div className="text-danger small mb-1">
                                                                    <strong>Descuento:</strong> -{Number(asset.dGastoDescuento).toLocaleString("es-MX", { style: "currency", currency: asset.vGastoMoneda || "MXN" })}
                                                                </div>
                                                            )}
                                                            {asset.dGastoIva != null && (
                                                                <div className="text-muted small mb-1">
                                                                    <strong>IVA:</strong> {Number(asset.dGastoIva).toLocaleString("es-MX", { style: "currency", currency: asset.vGastoMoneda || "MXN" })}
                                                                </div>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                </Card.Body>
                            </Card>
                        </Form>

                        {/* Modal para Buscar Gasto */}
                        <Modal show={mostrarModalGasto} onHide={() => setMostrarModalGasto(false)} size="lg" centered>
                            <Modal.Header closeButton>
                                <Modal.Title>
                                    <FiLink className="me-2 text-primary" /> Vincular Gasto Existente
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <Form.Group className="mb-4">
                                    <Form.Label className="fw-semibold">Buscar gasto por descripción o UUID de factura</Form.Label>
                                    <div className="position-relative">
                                        <Form.Control
                                            type="text"
                                            placeholder="Escribe la descripción o folio fiscal (UUID)..."
                                            value={searchTermGasto}
                                            onChange={(e) => setSearchTermGasto(e.target.value)}
                                            style={{ paddingLeft: '2.5rem' }}
                                        />
                                        <FiSearch className="position-absolute text-muted" style={{ left: '1rem', top: '50%', transform: 'translateY(-50%)' }} />
                                    </div>
                                    <Form.Text className="text-muted">
                                        Los resultados se filtran automáticamente a medida que escribes (mínimo 3 caracteres).
                                    </Form.Text>
                                </Form.Group>

                                <div className="border rounded bg-light p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {buscandoGastos ? (
                                        <div className="text-center py-4">
                                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                                            <span>Buscando gastos coincidentes...</span>
                                        </div>
                                    ) : gastosEncontrados.length > 0 ? (
                                        gastosEncontrados.map((gasto) => {
                                            const totalFormateado = Number(gasto.dGastoTotal).toLocaleString("es-MX", {
                                                style: "currency",
                                                currency: gasto.vGastoMoneda || "MXN"
                                            });
                                            const fechaFormateada = new Date(gasto.dFechaGasto).toLocaleDateString("es-MX", {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric"
                                            });

                                            return (
                                                <div key={gasto.idGasto} className="card border border-light shadow-none mb-2 hover-shadow p-3 rounded" style={{ transition: 'all 0.2s', cursor: 'pointer', backgroundColor: '#fff' }} onClick={() => handleSeleccionarGasto(gasto)}>
                                                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                                        <div style={{ flex: '1 1 60%' }}>
                                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                                <span className="badge bg-primary bg-opacity-10 text-primary">#{gasto.idGasto}</span>
                                                                <small className="text-muted">{fechaFormateada}</small>
                                                            </div>
                                                            <h6 className="mb-1 text-dark fw-bold">{gasto.vGastoDescripcion}</h6>
                                                            <div className="text-muted small">
                                                                <strong>Tipo:</strong> {gasto.vGastoTipoGasto} | <strong>Forma de pago:</strong> {gasto.vGastoFormaPago}
                                                            </div>
                                                            {gasto.vUUIDFactura && (
                                                                <div className="text-muted small font-monospace mt-1">
                                                                    UUID: {gasto.vUUIDFactura}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="text-md-end" style={{ flex: '1 1 30%', minWidth: '150px' }}>
                                                            <div className="fw-bold fs-5 text-dark mb-2">{totalFormateado}</div>
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleSeleccionarGasto(gasto);
                                                                }}
                                                            >
                                                                <FiLink className="me-1" /> Seleccionar
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="text-center py-4 text-muted">
                                            {searchTermGasto.trim().length >= 3 ? "No se encontraron gastos con ese criterio." : "Escribe al menos 3 caracteres para iniciar la búsqueda."}
                                        </div>
                                    )}
                                </div>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={() => setMostrarModalGasto(false)}>
                                    Cerrar
                                </Button>
                            </Modal.Footer>
                        </Modal>
                    </>
                )}






            {id && <Card className="mt-3">
                <Card.Body>
                    <Row>
                        <Col md={12}>
                            <h5 className=" mb-4">
                                <FiRotateCcw className="me-2" style={{ verticalAlign: 'middle' }} />
                                Historial de Asignaci&oacute;n
                            </h5>
                            {cargando ? (
                                <Skeleton count={3} />
                            ) : (
                                <div className="timeline-container">
                                    {historialAsignaciones.length > 0 || (asset && (asset.vNombreResponsable || asset.vApellidosResponsable)) ? (
                                        <div className="position-relative">
                                            {/* Línea vertical principal */}
                                            <div
                                                className="position-absolute bg-light"
                                                style={{
                                                    left: '15px',
                                                    top: '0',
                                                    bottom: '0',
                                                    width: '2px'
                                                }}
                                            ></div>

                                            {/* Empleado Actual */}
                                            {asset && (asset.vNombreResponsable || asset.vApellidosResponsable) && (
                                                <div className="d-flex align-items-center mb-3 position-relative">
                                                    {/* Indicador de timeline - Verde para actual */}
                                                    <div
                                                        className={`d-flex align-items-center justify-content-center position-relative`}
                                                        style={{
                                                            width: '30px',
                                                            height: '30px',
                                                            minWidth: '30px',
                                                            zIndex: 1
                                                        }}
                                                    >
                                                        <div
                                                            className="rounded-circle bg-success d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: '20px',
                                                                height: '20px'
                                                            }}
                                                        >
                                                            <FiUserCheck className="text-white" style={{ fontSize: '10px' }} />
                                                        </div>
                                                    </div>

                                                    {/* Contenido del actual */}
                                                    <div className="flex-grow-1">
                                                        <div className="card border-0 bg-success bg-opacity-10" style={{ fontSize: '0.9rem' }}>
                                                            <div className="card-body p-3">
                                                                <div className="d-flex justify-content-between align-items-start">
                                                                    <div className="flex-grow-1">
                                                                        {/* Nombre del empleado actual */}
                                                                        <div className="d-flex align-items-center mb-2">
                                                                            <h6 className="mb-0 text-success fw-bold me-2">
                                                                                <FiUser className="me-2" />
                                                                                {asset.vNombreResponsable && asset.vApellidosResponsable
                                                                                    ? `${asset.vNombreResponsable} ${asset.vApellidosResponsable}`
                                                                                    : asset.vNombreResponsable || asset.vApellidosResponsable || 'Sin responsable'
                                                                                }
                                                                            </h6>
                                                                            <span className="badge bg-success text-white">
                                                                                <FiUserCheck className="me-1" style={{ fontSize: '10px' }} />
                                                                                ACTUAL
                                                                            </span>
                                                                        </div>



                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Historial de Asignaciones */}
                                            {historialAsignaciones.map((asignacion, index) => {
                                                const empleadoActual = asignacion.idEmpleadoResponsable === 0
                                                    ? "Todos los empleados"
                                                    : asignacion.vNombreResponsable
                                                        ? `${asignacion.vNombreResponsable} ${asignacion.vApellidosResponsable}`
                                                        : "Sin responsable";



                                                const hayTransicion = asignacion.idEmpleadoResponsableAnterior !== undefined &&
                                                    asignacion.idEmpleadoResponsableAnterior !== null;

                                                return (
                                                    <div key={index} className="d-flex align-items-center mb-3 position-relative">
                                                        {/* Indicador de timeline */}
                                                        <div
                                                            className={`d-flex align-items-center justify-content-center position-relative`}
                                                            style={{
                                                                width: '30px',
                                                                height: '30px',
                                                                minWidth: '30px',
                                                                zIndex: 1
                                                            }}
                                                            title={empleadoActual}
                                                        >
                                                            <div
                                                                className="rounded-circle bg-primary d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    width: '20px',
                                                                    height: '20px'
                                                                }}
                                                            >
                                                                <i className="fas fa-history text-white" style={{ fontSize: '10px' }}></i>
                                                            </div>
                                                        </div>

                                                        {/* Contenido */}
                                                        <div className="flex-grow-1">
                                                            <div className="card border-0 bg-light" style={{ fontSize: '0.9rem' }}>
                                                                <div className="card-body p-3">
                                                                    <div className="d-flex justify-content-between align-items-start">
                                                                        <div className="flex-grow-1">
                                                                            {/* Nombre del empleado con transición */}
                                                                            <div className="d-flex align-items-center mb-2">
                                                                                {/* Empleado anterior (solo imagen) */}
                                                                                {hayTransicion && asignacion.idEmpleadoResponsableAnterior && asignacion.idEmpleadoResponsableAnterior !== 0 && (
                                                                                    <>
                                                                                        <div className="me-2" style={{ width: '32px', height: '32px' }}
                                                                                            title={asignacion.vNombreResponsableAnterior + ' ' + asignacion.vApellidosResponsableAnterior}
                                                                                        >

                                                                                            <ImagenPerfil idUsuario={asignacion.idEmpleadoResponsableAnterior} placeholder={asignacion.vNombreResponsableAnterior + " " + asignacion.vApellidosResponsableAnterior} />
                                                                                        </div>

                                                                                        {/* Flecha */}
                                                                                        <FiArrowRight className="me-2 text-gray" style={{ fontSize: '16px' }} />
                                                                                    </>
                                                                                )}

                                                                                {/* Empleado actual */}
                                                                                {asignacion.idEmpleadoResponsable && asignacion.idEmpleadoResponsable !== 0 && (
                                                                                    <div className="me-2" style={{ width: '32px', height: '32px' }}
                                                                                        title={asignacion.vNombreResponsable + ' ' + asignacion.vApellidosResponsable}
                                                                                    >
                                                                                        <ImagenPerfil idUsuario={asignacion.idEmpleadoResponsable} placeholder={asignacion.vNombreResponsable + " " + asignacion.vApellidosResponsable} />
                                                                                    </div>
                                                                                )}
                                                                                <h6 className="mb-0 text-dark fw-bold">
                                                                                    {empleadoActual}
                                                                                </h6>
                                                                            </div>

                                                                            {/* Línea separadora */}
                                                                            <hr className="my-2" style={{ opacity: 0.6, borderWidth: '1px' }} />

                                                                            {/* Información adicional */}
                                                                            <div className="d-flex flex-wrap gap-3 small text-muted">
                                                                                {asignacion.vCreatedUser && (
                                                                                    <div className="d-flex align-items-center">
                                                                                        <FiUserCheck className="me-1" />
                                                                                        <span>{asignacion.vCreatedUser}</span>
                                                                                    </div>
                                                                                )}
                                                                                {asignacion.dFechaCambio && (
                                                                                    <div className="d-flex align-items-center">
                                                                                        <FiCalendar className="me-1" />
                                                                                        <span>
                                                                                            {new Date(asignacion.dFechaCambio).toLocaleDateString('es-ES', {
                                                                                                day: 'numeric',
                                                                                                month: 'long',
                                                                                                year: 'numeric'
                                                                                            })}
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                                {asignacion.dFechaCambio && (
                                                                                    <div className="d-flex align-items-center">
                                                                                        <FiClock className="me-1" />
                                                                                        <span>
                                                                                            {new Date(asignacion.dFechaCambio).toLocaleTimeString('es-ES', {
                                                                                                hour: '2-digit',
                                                                                                minute: '2-digit'
                                                                                            })}
                                                                                        </span>
                                                                                    </div>
                                                                                )}

                                                                                {asignacion.vIp && (
                                                                                    <div className="d-flex align-items-center">
                                                                                        <FiWifi className="me-1" />
                                                                                        <span>{asignacion.vIp}</span>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}                                        </div>
                                    ) : (
                                        <div className="text-center py-4">
                                            <i className="fas fa-history fa-2x text-muted mb-2"></i>
                                            <h6 className="text-muted">No hay historial disponible</h6>
                                            <small className="text-muted">Los cambios aparecer&aacute;n aqu&iacute;</small>
                                        </div>
                                    )}
                                </div>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
            }

        </>
    );
};

export default AssetForm


